/**
 * Hooks for the auditor profile's "Pisos de Venta" section and its assignment modal.
 * The views (AuditorStores / StoreModal / AuditorStoreTable) only render what these return.
 */
import React from 'react';

// Handles both string and ObjectId comparisons between Store.auditor and the auditor id.
const isAssignedTo = (store, auditorId) => {
    const storeAuditor = store?.auditor;
    return storeAuditor === auditorId || storeAuditor?._id === auditorId || storeAuditor?.toString() === auditorId?.toString();
};

/** Stores assigned to the auditor (from the full store list). */
export function useAuditorStores(stores, auditorId) {
    const auditorStores = React.useMemo(() => stores?.filter((store) => isAssignedTo(store, auditorId)) || [], [stores, auditorId]);

    // ✅ Debug logging
    React.useEffect(() => {
        console.log('🔍 AuditorStores component:', {
            totalStores: stores?.length,
            auditorId,
            auditorIdType: typeof auditorId,
            auditorStoresCount: auditorStores.length,
            auditorStores: auditorStores,
            sampleStores: stores?.slice(0, 3)?.map((store) => ({
                id: store._id,
                name: store.name,
                auditor: store.auditor,
                auditorType: typeof store.auditor,
                auditorMatch: isAssignedTo(store, auditorId),
            })),
        });
    }, [stores, auditorId, auditorStores]);

    return auditorStores;
}

const requestStores = async (page, search) => {
    const API = (await import('../../../../data/api')).default;
    const api = API.Instance();
    return api.requestStores(page, 1000, search);
};

/** State and actions for the "Pisos de Venta" assignment modal (paging, search, add/remove). */
export function useAuditorStoresModal({ isOpen, allStores, userId, updateStores }) {
    const auditorStores = allStores?.filter((store) => store.auditor === userId)?.map((store) => store._id);

    const [search, setSearch] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalStores, setTotalStores] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(0);
    const [displayedStores, setDisplayedStores] = React.useState([]);
    const [removedStores, setRemovedStores] = React.useState([]);
    const [selectedStores, setSelectedStores] = React.useState([]);
    const [searchResults, setSearchResults] = React.useState([]);
    const [isSearching, setIsSearching] = React.useState(false);

    const loadStoresPage = async (page = 1) => {
        setLoading(true);
        try {
            const response = await requestStores(page, search);

            if (response && Array.isArray(response)) {
                // Handle old response format (backward compatibility)
                setDisplayedStores(response);
                setTotalStores(response.length);
                setTotalPages(1);
            } else if (response && response.stores) {
                // Handle new paginated response format
                setDisplayedStores(response.stores);
                setTotalStores(response.pagination?.total || response.stores.length);
                setTotalPages(response.pagination?.pages || 1);
            } else {
                // Fallback to passed allStores prop
                setDisplayedStores(allStores || []);
                setTotalStores(allStores?.length || 0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error loading stores:', error);
            // Fallback to passed allStores prop
            setDisplayedStores(allStores || []);
            setTotalStores(allStores?.length || 0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        console.log('🔍 AuditorStoresModal opened:', { auditorStores, userId, allStores: allStores?.length });
        setSelectedStores(auditorStores);
        if (isOpen) {
            loadStoresPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    React.useEffect(() => {
        if (isOpen) {
            loadStoresPage(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const onSearch = async (value) => {
        setSearch(value);
        setIsSearching(true);

        if (value.trim() === '') {
            setSearchResults([]);
            setCurrentPage(1);
            loadStoresPage(1);
        } else {
            try {
                const response = await requestStores(1, value);

                if (response && Array.isArray(response)) {
                    setSearchResults(response);
                } else if (response && response.stores) {
                    setSearchResults(response.stores);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Error searching stores:', error);
                setSearchResults([]);
            }
        }
        setIsSearching(false);
    };

    const isSelected = (store) => selectedStores?.includes(store?._id);

    const toggleStore = (store) => {
        console.log('🔄 Store selection clicked:', {
            storeId: store?._id,
            storeName: store?.name,
            isSelected: selectedStores?.includes(store?._id),
            currentSelected: selectedStores,
            currentRemoved: removedStores,
        });

        if (selectedStores?.includes(store?._id)) {
            // Removing store from auditor
            const newSelected = selectedStores?.filter((id) => id !== store?._id);
            const newRemoved = [...removedStores, store?._id];
            console.log('➖ Removing store:', { newSelected, newRemoved });
            setSelectedStores(newSelected);
            setRemovedStores(newRemoved);
        } else {
            // Adding store to auditor
            const newSelected = [...selectedStores, store?._id];
            const newRemoved = removedStores?.filter((id) => id !== store?._id);
            console.log('➕ Adding store:', { newSelected, newRemoved });
            setSelectedStores(newSelected);
            setRemovedStores(newRemoved);
        }
    };

    const save = () => {
        console.log('✅ Modal OK clicked:', { selectedStores, removedStores, userId });
        updateStores(selectedStores, removedStores, userId);
    };

    const listedStores = (search !== '' ? searchResults : displayedStores) ?? [];

    return {
        search,
        loading,
        isSearching,
        currentPage,
        setCurrentPage,
        totalPages,
        totalStores,
        displayedStores,
        searchResults,
        listedStores,
        selectedCount: selectedStores?.length ?? 0,
        onSearch,
        isSelected,
        toggleStore,
        save
    };
}
